import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InventoryIcon from '@mui/icons-material/Inventory';
import RecyclingIcon from '@mui/icons-material/Recycling';
import BlockIcon from '@mui/icons-material/Block';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import StockPage from './StockPage';
import FinishedGoodsStockPage from './FinishedGoodsStockPage';
import StockCrushingWastePage from './StockCrushingWastePage';
import BlockingStockPage from './BlockingStockPage';
import BrakWarehousePage from './BrakWarehousePage';
import SampleWarehousePage from './SampleWarehousePage';
import InventoryMovementsPage from './InventoryMovementsPage';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const ALL_TABS = [
  { id: 'raw',             label: 'Сировина',             perm: 'menu.stock.raw',             icon: <WarehouseIcon fontSize="small" />,          render: () => <StockPage stockKind="raw" /> },
  { id: 'dyes',            label: 'Барвники',             perm: 'menu.stock.dyes',            icon: <WarehouseIcon fontSize="small" />,          render: () => <StockPage stockKind="dyes" /> },
  { id: 'packaging',       label: 'Упаковка',             perm: 'menu.stock.packaging',       icon: <WarehouseIcon fontSize="small" />,          render: () => <StockPage stockKind="packaging" /> },
  { id: 'technical',       label: 'Техматеріали',         perm: 'menu.stock.technical',       icon: <WarehouseIcon fontSize="small" />,          render: () => <StockPage stockKind="technical" /> },
  { id: 'semi',            label: 'Напівфабрикати',       perm: 'menu.stock.semi',            icon: <WarehouseIcon fontSize="small" />,          render: () => <StockPage stockKind="semi" /> },
  { id: 'packaging-work',  label: 'Для пакування',        perm: 'menu.stock.packaging_work',  icon: <WarehouseIcon fontSize="small" />,          render: () => <StockPage stockKind="packaging_work" /> },
  { id: 'finished',        label: 'Готова продукція',     perm: 'menu.stock.finished',        icon: <InventoryIcon fontSize="small" />,          render: () => <FinishedGoodsStockPage /> },
  { id: 'crushing-waste',  label: 'Склад перемолів',     perm: 'menu.stock.crushing_waste',  icon: <RecyclingIcon fontSize="small" />,          render: () => <StockCrushingWastePage /> },
  {
    id: 'blocking',
    label: 'Блокування',
    perm: 'menu.stock.blocking',
    requireTrue: true,
    icon: <BlockIcon fontSize="small" />,
  },
  { id: 'consumable',      label: 'Розхідні матеріали',   perm: 'menu.stock.consumable',      icon: <WarehouseIcon fontSize="small" />,          render: () => <StockPage stockKind="consumable" /> },
  {
    id: 'defect',
    label: 'Брак',
    perm: 'menu.production.warehouse.defect',
    requireTrue: true,
    icon: <ReportProblemOutlinedIcon fontSize="small" />,
    render: () => <BrakWarehousePage />,
  },
  { id: 'samples',         label: 'Взірці',               perm: 'menu.stock.samples',         icon: <ScienceOutlinedIcon fontSize="small" />,    render: () => <SampleWarehousePage /> },
  { id: 'movements',       label: 'Журнал рухів',         perm: 'menu.stock.movements',       icon: <SwapHorizIcon fontSize="small" />,         render: () => <InventoryMovementsPage /> },
];

export default function StockHubPage({ currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const perms = currentUser?.effective_permissions || {};

  const tabs = useMemo(() => {
    return ALL_TABS.filter((t) => {
      if (!t.perm) return true;
      return perms[t.perm] === true;
    });
  }, [perms]);

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return tabs.some((t) => t.id === tab) ? tab : (tabs[0]?.id || 'raw');
  }, [searchParams, tabs]);

  const setActiveTab = (tab) => {
    setSearchParams(tab === tabs[0]?.id ? {} : { tab });
  };

  const allButtons = tabs;
  const half = Math.ceil(allButtons.length / 2);
  const rows = [allButtons.slice(0, half), allButtons.slice(half)];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)', overflow: 'hidden' }}>
      {/* Category tab buttons */}
      <Box sx={{ flexShrink: 0, mb: 1 }}>
        {rows.map((row, ri) => (
          <Box key={ri} sx={{ display: 'grid', gridTemplateColumns: `repeat(${half}, 1fr)`, gap: 1, mb: ri < rows.length - 1 ? 1 : 0 }}>
            {row.map((t) => (
              <Button
                key={t.id}
                variant={activeTab === t.id ? 'contained' : 'outlined'}
                startIcon={t.icon}
                onClick={() => setActiveTab(t.id)}
                sx={{ py: 1.5, fontSize: '0.875rem' }}
                fullWidth
              >
                {t.label}
              </Button>
            ))}
          </Box>
        ))}
      </Box>

      {/* Лише активна вкладка — щоб Заявки/Нестача не «залипали» при перемиканні */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {tabs.map((t) => (
          activeTab === t.id ? (
            <Box key={t.id} sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
              {t.id === 'blocking'
                ? <BlockingStockPage currentUser={currentUser} />
                : t.render()}
            </Box>
          ) : null
        ))}
      </Box>
    </Box>
  );
}
